import {Component, Object3D, MeshComponent, Mesh, Material} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';

import {quat2, vec3} from 'gl-matrix';

const tempVecOrigin = vec3.fromValues(0, 0, 0);
const tempVecDistance = vec3.fromValues(0, 0, 0);

/**
 * Spawns snow particles in a area 5m height and a width and length defined by 'size'.
 */
export class SnowParticles extends Component {
    static TypeName = 'snow-particles';

    @property.mesh({required: true})
    mesh!: Mesh;

    @property.material({required: true})
    material!: Material;

    @property.float(0.1)
    delay = 0.1;

    @property.int(1500)
    maxParticles = 1500;

    @property.float(15)
    initialSpeed = 15;

    @property.float(0.01)
    particleScale = 0.01;

    @property.int(16)
    size = 16;

    @property.bool(true)
    startOnLoad = true;

    /**
     * Number of particles currently spawned.
     */
    private count = 0;

    /**
     * List of particle objects.
     */
    private objects: Object3D[] = [];

    /**
     * List of velocities for each particle.
     */
    private velocities: vec3[] = [];

    private started = false;

    start() {
        if (this.startOnLoad) {
            this.generateParticles();
            this.started = true;
        }
    }

    generateParticles() {
        this.objects = this.engine.scene.addObjects(
            this.maxParticles,
            null,
            this.maxParticles
        );

        for (let i = 0; i < this.maxParticles; ++i) {
            this.velocities.push([
                Math.random() / 4 - 0.125,
                -Math.random() - 0.2,
                Math.random() / 4 - 0.125,
            ]);
            const obj = this.objects[i];
            obj.name = 'particle' + this.count.toString();
            const mesh = obj.addComponent(MeshComponent);

            mesh!.mesh = this.mesh;
            mesh!.material = this.material;

            /* Most efficient way to hide the mesh */
            obj.scaleLocal([0, 0, 0]);
        }

        /* Time to spawn particles */
        for (let i = 0; i < this.maxParticles; ++i) {
            this.spawn();
        }
    }

    startSpawning() {
        this.generateParticles();
        this.started = true;
    }

    override onActivate() {
        if (!this.startOnLoad && !this.started) {
            return;
        }
        if (!this.started) {
            this.generateParticles();
            this.started = true;
        } else {
            for (let i = 0; i < this.maxParticles; ++i) {
                this.spawn();
            }
        }
    }

    override onDeactivate(): void {
        this.objects.forEach((obj) => {
            obj.scaleLocal([0, 0, 0]);
        });
    }

    update(dt: number) {
        /* Target for retrieving particles world locations */

        for (let i = 0; i < Math.min(this.count, this.objects.length); ++i) {
            /* Get translation first, as object.translate() will mark
             * the object as dirty, which will cause it to recalculate
             * obj.transformWorld on access. We want to avoid this and
             * have it be recalculated in batch at the end of frame
             * instead */
            quat2.getTranslation(tempVecOrigin, this.objects[i].getTransformWorld());

            /* Apply gravity */
            const vel = this.velocities[i];

            /* Check if particle would collide */
            if (tempVecOrigin[0] + vel[0] * dt > 8) {
                tempVecOrigin[0] -= 16;
            } else if (tempVecOrigin[0] + vel[0] * dt <= -8) {
                tempVecOrigin[0] += 16;
            }

            if (tempVecOrigin[2] + vel[2] * dt > 8) {
                tempVecOrigin[2] -= 16;
            } else if (tempVecOrigin[2] + vel[2] * dt <= -8) {
                tempVecOrigin[2] += 16;
            }

            if (tempVecOrigin[1] + vel[1] * dt <= 0) {
                /* Pseudo friction */
                tempVecOrigin[1] = 5;
                this.objects[i].setPositionWorld(tempVecOrigin);
            }
        }

        for (let i = 0; i < Math.min(this.count, this.objects.length); ++i) {
            /* Apply velocity */
            vec3.scale(tempVecDistance, this.velocities[i], dt);
            this.objects[i].translateWorld(tempVecDistance);
        }
    }

    /** Spawn a particle */
    spawn() {
        const index = this.count % this.maxParticles;

        const obj = this.objects[index];
        obj.resetTransform();
        obj.scaleLocal([this.particleScale, this.particleScale, this.particleScale]);

        /* Activate component, otherwise it will not show up! */
        obj.getComponent(MeshComponent)!.active = true;

        obj.translateWorld([
            Math.random() * this.size - this.size / 2,
            Math.random() * 5,
            Math.random() * this.size - this.size / 2,
        ]);

        this.count += 1;
    }
}
